import React from 'react';
import PropTypes from 'prop-types';
import Bluebird from 'bluebird';
import autobind from 'autobind-decorator';
import {action} from 'mobx';
import {observer} from 'mobx-react';

import {withI18n} from './Context';

import OptionsList from './OptionsList';
import ProgressBar from 'spa/Components/ProgressBar';
import abortable from 'utils/abortable';
import {cn} from 'spa/css';

import './TypeaheadField.less';

import clearIcon from 'spa/images/icons/cancel_onwhite_24.svg';

const NOISE_CHARS = [' ', '.', '-', ','];

@observer
@withI18n
class TypeaheadField extends React.Component {
  static propTypes = {
    getSuggestionsForString: PropTypes.func.isRequired,
    hideClearOnEmpty: PropTypes.bool,
    name: PropTypes.string.isRequired,
    onSelect: PropTypes.func,
    onClear: PropTypes.func,
    onDeselect: PropTypes.func,
    defaultValue: PropTypes.string,
    selectFirstOptionOnEnterKey: PropTypes.bool,
    wrap: PropTypes.func,
    includeClear: PropTypes.bool,
    clearStyles: PropTypes.object,
    zIndex: PropTypes.number,
  };

  static defaultProps = {
    hideClearOnEmpty: false,
    wrap: x => x,
  };

  state = {
    isLoading: false,
    isLoaded: false,
    suggestions: [],
    value: this.props.defaultValue,
    open: false,
  };

  componentWillUnmount() {
    if (this.abortLast) this.abortLast();
  }

  @action onSelect = (value) => {
    const {suggestions} = this.state;
    if (!suggestions) { return; }

    const suggestion = suggestions
      .find(_suggestion => _suggestion.value === value);

    if (suggestion) {
      this.wrappedInstance.value = suggestion.term || suggestion.name;
      this.setState({open: false, value: suggestion.term || suggestion.name});
      if (this.props.onSelect) this.props.onSelect(suggestion);
    }
  };

  suggestionsCache = {};
  abortLast = null;

  @action onChange = (event) => {
    if (this.props.onDeselect) this.props.onDeselect();

    const {value} = event.target;
    this.loadSuggestionsForValue(value);
  };

  @action onClear = (event) => {
    if (event) event.preventDefault();
    const {onClear} = this.props;
    this.setValue('');
    onClear();
  }

  @action loadSuggestionsForValue(value) {
    const {i18n} = this.context;
    if (this.abortLast) this.abortLast();

    let suggestions = null;

    this.setState({
      isLoading: false,
      isLoaded: false,
      error: null,
    });

    if (value && value.trim().length > 2) {
      const val = value.trim();
      if (this.suggestionsCache[val]) {
        suggestions = this.suggestionsCache[val];
      } else {
        const {abort, isAborted} = abortable();
        this.abortLast = abort;

        setTimeout(() => {
          Bluebird
            .resolve(value)
            .delay(500)
            .then(async (value) => {
              if (isAborted()) return;
              const suggestions = await this.props.getSuggestionsForString(value);
              this.suggestionsCache[val] = suggestions;
              if (isAborted()) return;
              this.setState({
                suggestions,
                isLoading: false,
                isLoaded: true,
              });
            })
            .catch((error) => {
              if (isAborted()) return;
              this.setState({
                suggestions: [],
                isLoading: false,
                isLoaded: false,
                error: i18n.__error(error),
              });
            });
        }, 0);

        this.setState({
          isLoading: true,
          isLoaded: false,
        });
      }
    }

    this.setState({
      value,
      suggestions,
      open: true,
    });
  }

  @action onClose = () => {
    this.setState({
      open: false,
    });
  };

  @action onFocus = (event) => {
    if (event) {
      this.loadSuggestionsForValue(event.target.value);
    }
    if (this.blurTimeout) clearTimeout(this.blurTimeout);
  };

  blurTimeout = null;
  @action onBlur = (/*event*/) => {
    if (this.state.open) {
      this.blurTimeout = setTimeout(this.onClose, 200);
    }
  };

  @action onHighlight = (option) => {
    if (option) {
      const {wrappedInstance: input} = this;
      const originalValue = this.state.value;
      const value = originalValue.toUpperCase();
      const suggestion = option.term || option.name;

      let index = suggestion.toUpperCase().indexOf(value);

      let start = index + value.length;
      const end = suggestion.length;

      if (index === -1) {
        let valuePointer = 0;

        for (
          let suggestionPointer = 0;
          suggestionPointer < suggestion.length;
          ++suggestionPointer
        ) {
          if (
            suggestion[suggestionPointer] === value[valuePointer]
          ) {
            ++valuePointer;
          } else {
            while (NOISE_CHARS.includes(value[valuePointer])) {
              ++valuePointer;
            }

            if (value.length !== valuePointer) {
              if (!NOISE_CHARS.includes(suggestion[suggestionPointer])) {
                if (
                  suggestion[suggestionPointer] !== value[valuePointer]
                ) {
                  // The result doesn't begin with what the user typed.
                  // Skip the whole autocomplete thing.
                  break;
                }
                ++valuePointer;
              }
            } else {
              --suggestionPointer;
            }
          }

          if (value.length === valuePointer) {
            start = suggestionPointer + 1;
            index = 0;
            break;
          }
        }
      }

      if (index > -1) {
        if (input.createTextRange) {
          input.value = suggestion;
          const range = input.createTextRange();
          range.moveStart('character', start);
          range.moveEnd('character', end);
          range.select();
        } else if (input.setSelectionRange) {
          input.value = option.term || option.name;
          input.setSelectionRange(start, end);
        } else if (typeof input.selectionStart !== 'undefined') {
          input.value = option.term || option.name;
          input.selectionStart = start;
          input.selectionEnd = end;
        }
      } else if (input.value !== originalValue) {
        input.value = originalValue;
      }
    } else {
      const {wrappedInstance: input} = this;
      const originalValue = this.state.value;
      if (input.value !== originalValue) {
        input.value = originalValue;
      }
    }
  };

  getValue() {
    return this.wrappedInstance.value;
  }

  @action setValue(value) {
    this.wrappedInstance.value = value;
  }

  @autobind inputRef(input) {
    this.wrappedInstance = input;
  }

  render() {
    const {i18n} = this.context;
    /* eslint-disable no-unused-vars */
    const {
      getSuggestionsForString,
      hideClearOnEmpty,
      name,
      onSelect,
      onDeselect,
      selectFirstOptionOnEnterKey,
      wrap,
      onClear,
      includeClear,
      clearStyles,
      zIndex,
      ...rest
    } = this.props;
    /* eslint-enable no-unused-vars */
    const {
      suggestions, open, isLoading, isLoaded, error,
    } = this.state;

    let warning;
    if (suggestions && suggestions.length === 0) {
      warning = i18n.__('No results found');
    }

    let poweredByGoogleImage = null;
    if (suggestions && suggestions.length > 0 && suggestions[0].resultSource === 'gmaps') {
      poweredByGoogleImage = <img alt="" className="TypeaheadField-powered-by-google" src="https://res.cloudinary.com/huxr6hrje/image/upload/f_auto/v1470651347/powered_by_google_on_white_hdpi_pzzsia.png" />;
    }

    const wrappedElement = [
      <ProgressBar
        key="progress-bar"
        loading={isLoading}
        done={isLoaded}
        position="bottom"
      />,
      <input
        data-qa={`${name}-input`}
        key="field"
        {...rest}
        name={name}
        ref={this.inputRef}
        onChange={this.onChange}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
      />,
    ];

    if (includeClear) {
      wrappedElement.push(<div
        key="clear-icon"
        className={cn(
          'TypeaheadField__clear-icon',
          (hideClearOnEmpty && (!this.wrappedInstance || (this.wrapppedInstance && !this.wrappedInstance.value))) && 'TypeaheadField__clear-icon--hidden',
        )}
        style={clearStyles}
      >
        <button
          className="TypeaheadField__clear-icon-button"
          data-qa={`${name}-clear-button`}
          onClick={this.onClear}
          aria-label={i18n._x('Clear address selection', 'Typeahead field clear address label')}
          type="button"
        >
          <img
            alt=""
            src={clearIcon}
            width="30px"
            height="30px"
          />
        </button>
      </div>);
    }

    return (
      <OptionsList
        options={suggestions || []}
        hide={!open}
        name={name}
        onClose={this.onClose}
        onSelect={this.onSelect}
        onHighlight={this.onHighlight}
        error={error}
        warning={warning}
        className="TypeaheadField-options-list"
        noExpandX
        footer={poweredByGoogleImage}
        selectFirstOptionOnEnterKey={selectFirstOptionOnEnterKey}
        zIndex={zIndex}
      >
        {wrap(wrappedElement)}
      </OptionsList>
    );
  }
}

export default TypeaheadField;



// WEBPACK FOOTER //
// ./spa/Components/TypeaheadField.jsx